






















































































































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import VuePlotly from "@statnett/vue-plotly";
import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { fmtFloat, UNIT_SUFFIX } from "./formatters";
import { buildRcaUrl, RcaFilters } from "./filterQueryParams";
import PlotlyResizeMixin from "./plotly-resize-mixin";

interface MetricOption {
    key: string;
    label: string;
    valueformat: string;
    /** Unit added after the number on the bar chart hover, e.g. " t". Empty for counts. */
    unit: string;
    /** Unit written out for the flow diagram, which shows a number with no label
     *  in front of it, so "88,712" alone would not say what was being counted. */
    unitWord: string;
}

const METRIC_OPTIONS: MetricOption[] = [
    {
        key: "impacted_exposed_tonnes",
        label: "Impacted tonnes",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.tonnes,
        unitWord: "tonnes",
    },
    {
        key: "impacted_exposed_trip_count",
        label: "Impacted supply chain legs (trips)",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.none,
        unitWord: "trips",
    },
    {
        key: "rerouted_exposed_tonnes",
        label: "Rerouted tonnes",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.tonnes,
        unitWord: "tonnes",
    },
    {
        key: "rerouted_exposed_trip_count",
        label: "Rerouted supply chain legs (trips)",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.none,
        unitWord: "trips",
    },
    {
        key: "blocked_exposed_tonnes",
        label: "Blocked tonnes",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.tonnes,
        unitWord: "tonnes",
    },
    {
        key: "blocked_exposed_trip_count",
        label: "Blocked supply chain legs (trips)",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.none,
        unitWord: "trips",
    },
    {
        key: "baseline_exposed_tonnes",
        label: "Baseline tonnes",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.tonnes,
        unitWord: "tonnes",
    },
    {
        key: "baseline_exposed_trip_count",
        label: "Baseline supply chain legs (trips)",
        valueformat: ",.0f",
        unit: UNIT_SUFFIX.none,
        unitWord: "trips",
    },
];

const ROLE_COLOURS: Record<string, string> = {
    origin: "#013e75",
    destination: "#d97706",
};

/** Sankey ribbon colour by metric category (the prefix of the metric key): baseline = neutral
 * navy, impacted = amber, rerouted = teal (still moving), blocked = red (most severe). */
const LINK_COLOURS: Record<string, string> = {
    baseline: "rgba(1, 62, 117, 0.18)",
    impacted: "rgba(217, 119, 6, 0.24)",
    rerouted: "rgba(13, 148, 136, 0.24)",
    blocked: "rgba(214, 40, 40, 0.24)",
};
const LINK_COLOUR_DEFAULT = "rgba(1, 62, 117, 0.18)";

/** Leg-chart bar colours: selected leg in brand navy, the rest a muted shade. */
const LEG_SELECTED_COLOUR = "#013e75";
const LEG_UNSELECTED_COLOUR = "#9db8d2";

/** Display labels for the raw supply-chain-leg codes (the data keeps the raw codes; only the
 * UI shows these). Unknown codes fall back to the raw value. */
const LEG_LABELS: Record<string, string> = {
    production: "Production",
    input: "Input",
    primarydistribution: "Primary distribution",
    processing: "Processing",
    distribution: "Distribution",
    "import/export": "Import / export",
    "(unclassified)": "Unclassified",
};

@Component({ components: { VuePlotly } })
export default class EnterpriseTypesTab extends mixins(PlotlyResizeMixin) {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    /** Global sidebar filters — narrow enterprise-type exposure to matching trips. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;
    /** Whether this tab is currently active. Plotly charts that rendered while the tab was
     * hidden lay out at a collapsed width; we re-render them when the tab becomes visible. */
    @Prop({ default: true }) active!: boolean;

    fmtFloat = fmtFloat;

    /** All (supply_chain_leg, enterprise_role, enterprise_type, …metrics) rows — leg chart + tables. */
    rows: any[] = [];
    /** All (supply_chain_leg, enterprise_type_orig, enterprise_type_dest, …metrics) flow rows —
     * the per-leg Sankey. */
    flowRows: any[] = [];
    loading = false;
    error: string | null = null;

    metric: string = METRIC_OPTIONS[0].key;
    metricOptions = METRIC_OPTIONS;

    /** Supply chain leg the role chart + tables are drilled into. Defaults to the top leg. */
    selectedLeg: string | null = null;

    /** Active origin/destination tab in the per-leg tables (0 = origin, 1 = destination). */
    tableTab = 0;

    /** Pretty display label for a raw supply-chain-leg code. */
    legLabel(code: string | null): string {
        if (code == null) return "";
        return LEG_LABELS[code] || code;
    }

    mounted() {
        this.fetch();
    }

    /** One key over every fetch input so a param change triggers exactly one fetch. Stacked
     * @Watch decorators fire once per changed prop, so a date-range change (startDate + endDate
     * in the same tick) used to fetch this panel twice. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}|${JSON.stringify(
            this.filters,
        )}`;
    }

    @Watch("fetchParamsKey")
    onParamsChanged() {
        this.fetch();
    }

    resizeCharts() {
        this.refitPlotly(["legChart", "sankeyChart"]);
    }

    /** Keep the selection valid: default to the top-ranked leg, and re-point it if a data
     * reload dropped the previously-selected leg. */
    @Watch("rows")
    onRowsChanged() {
        const legs = this.legChartRows;
        if (
            !this.selectedLeg ||
            !legs.some((l) => l.leg === this.selectedLeg)
        ) {
            this.selectedLeg = legs.length ? legs[0].leg : null;
        }
    }

    fetch() {
        if (!this.startDate || !this.endDate) return;
        this.loading = true;
        this.error = null;
        eventLoading.startLoading();
        const params = {
            start_date: this.startDate,
            end_date: this.endDate,
        };
        Promise.all([
            Vue.axios({
                url: buildRcaUrl(
                    endpoints.roadClosureAnalyticsEnterpriseTypeExposureByLegUrl,
                    params,
                    this.filters,
                ),
            }),
            Vue.axios({
                url: buildRcaUrl(
                    endpoints.roadClosureAnalyticsSupplyChainLegFlowsUrl,
                    params,
                    this.filters,
                ),
            }),
        ])
            .then(([byLeg, flows]: any[]) => {
                this.rows = byLeg.data || [];
                this.flowRows = flows.data || [];
            })
            .catch((err: any) => {
                console.error("[RCA EnterpriseTypes]", err);
                this.error = err?.message || "Failed to load.";
                this.rows = [];
                this.flowRows = [];
            })
            .finally(() => {
                this.loading = false;
                eventLoading.stopLoading();
            });
    }

    get currentMetric(): MetricOption {
        return (
            METRIC_OPTIONS.find((m) => m.key === this.metric) ??
            METRIC_OPTIONS[0]
        );
    }

    /** Node colours (origin / destination), exposed for the flow-legend swatches. */
    get roleColours(): Record<string, string> {
        return ROLE_COLOURS;
    }

    /**
     * Per-leg total for the selected metric, ranked descending. Summed over the origin-role
     * rows only — each trip has exactly one origin type and belongs to one leg, so this counts
     * every trip once (summing both roles would double count).
     */
    get legChartRows(): { leg: string; value: number }[] {
        const totals = new Map<string, number>();
        for (const r of this.rows) {
            if (r.enterprise_role !== "origin") continue;
            const leg = r.supply_chain_leg || "(unclassified)";
            totals.set(
                leg,
                (totals.get(leg) || 0) + (Number(r[this.metric]) || 0),
            );
        }
        return Array.from(totals.entries())
            .map(([leg, value]) => ({ leg, value }))
            .sort((a, b) => b.value - a.value);
    }

    get legChartTraces(): any[] {
        const rows = this.legChartRows;
        return [
            {
                type: "bar",
                x: rows.map((r) => this.legLabel(r.leg)),
                y: rows.map((r) => r.value),
                marker: {
                    color: rows.map((r) =>
                        r.leg === this.selectedLeg
                            ? LEG_SELECTED_COLOUR
                            : LEG_UNSELECTED_COLOUR,
                    ),
                },
                hovertemplate:
                    "%{x}<br>" +
                    this.currentMetric.label +
                    ": %{y:" +
                    this.currentMetric.valueformat +
                    "}" +
                    this.currentMetric.unit +
                    "<extra></extra>",
            },
        ];
    }

    get legChartLayout(): any {
        return {
            dragmode: false,
            height: 320,
            margin: { l: 60, r: 16, t: 8, b: 90 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            xaxis: { automargin: true, tickangle: -25 },
            yaxis: {
                automargin: true,
                title: this.currentMetric.label,
                gridcolor: "rgba(0,0,0,0.06)",
            },
        };
    }

    /** A click on a leg bar drills the role chart + tables into that leg. The bar's x is the
     * display label, so map it back to the raw leg code the data is keyed on. */
    onLegClick(e: any) {
        const label = e && e.points && e.points[0] && e.points[0].x;
        if (label == null) return;
        const match = this.legChartRows.find(
            (r) => this.legLabel(r.leg) === String(label),
        );
        if (match) this.selectedLeg = match.leg;
    }

    /** Rows for the currently-selected leg only — drives the role chart + the two tables. */
    get legFilteredRows(): any[] {
        if (this.selectedLeg == null) return [];
        return this.rows.filter(
            (r) =>
                (r.supply_chain_leg || "(unclassified)") === this.selectedLeg,
        );
    }

    get originRows(): any[] {
        return this.legFilteredRows.filter(
            (r) => r.enterprise_role === "origin",
        );
    }
    get destinationRows(): any[] {
        return this.legFilteredRows.filter(
            (r) => r.enterprise_role === "destination",
        );
    }

    /** All rows for the selected leg/role — uncapped. */
    get originTableRows(): any[] {
        return this.originRows;
    }
    get destinationTableRows(): any[] {
        return this.destinationRows;
    }

    /** Flow rows for the selected leg with a positive value for the current metric. */
    get legFlowRows(): any[] {
        if (this.selectedLeg == null) return [];
        return this.flowRows.filter(
            (r) =>
                (r.supply_chain_leg || "(unclassified)") === this.selectedLeg &&
                (Number(r[this.metric]) || 0) > 0,
        );
    }

    /** All origin→destination flows for the selected leg, biggest first. */
    get displayedFlows(): any[] {
        return [...this.legFlowRows].sort(
            (a, b) =>
                (Number(b[this.metric]) || 0) - (Number(a[this.metric]) || 0),
        );
    }

    /** One Plotly Sankey trace: origin enterprise types (left) -> destination types (right),
     * ribbon width = the selected metric. Origin and destination nodes are kept separate (a
     * type can be both an origin and a destination), so node labels can repeat across sides. */
    get sankeyTraces(): any[] {
        const flows = this.displayedFlows;
        const origLabels: string[] = [];
        const origIdx = new Map<string, number>();
        const destLabels: string[] = [];
        const destIdx = new Map<string, number>();
        for (const r of flows) {
            if (!origIdx.has(r.enterprise_type_orig)) {
                origIdx.set(r.enterprise_type_orig, origLabels.length);
                origLabels.push(r.enterprise_type_orig);
            }
            if (!destIdx.has(r.enterprise_type_dest)) {
                destIdx.set(r.enterprise_type_dest, destLabels.length);
                destLabels.push(r.enterprise_type_dest);
            }
        }
        const originCount = origLabels.length;
        const source: number[] = [];
        const target: number[] = [];
        const value: number[] = [];
        for (const r of flows) {
            source.push(origIdx.get(r.enterprise_type_orig) as number);
            target.push(
                originCount + (destIdx.get(r.enterprise_type_dest) as number),
            );
            value.push(Number(r[this.metric]) || 0);
        }
        const fmt = this.currentMetric.valueformat;
        const unit = this.currentMetric.unitWord;
        // Ribbon colour follows the metric category (baseline / impacted / rerouted / blocked).
        const linkColor =
            LINK_COLOURS[this.metric.split("_")[0]] || LINK_COLOUR_DEFAULT;
        return [
            {
                type: "sankey",
                orientation: "h",
                // Lock node positions — nodes are not draggable (default "snap" lets users
                // drag them around).
                arrangement: "fixed",
                valueformat: fmt,
                valuesuffix: " " + unit,
                node: {
                    label: [...origLabels, ...destLabels],
                    // Origin nodes only send flow (incoming = 0) and destination nodes only
                    // receive (outgoing = 0), so the hover shows just the relevant direction
                    // rather than the default incoming/outgoing pair with a zero.
                    customdata: [
                        ...origLabels.map(() => "Outgoing"),
                        ...destLabels.map(() => "Incoming"),
                    ],
                    color: [
                        ...origLabels.map(() => ROLE_COLOURS.origin),
                        ...destLabels.map(() => ROLE_COLOURS.destination),
                    ],
                    pad: 14,
                    thickness: 16,
                    line: { width: 0 },
                    hovertemplate:
                        "%{label}<br>%{customdata}: %{value:" +
                        fmt +
                        "} " +
                        unit +
                        "<extra></extra>",
                },
                link: {
                    source,
                    target,
                    value,
                    color: linkColor,
                    hovertemplate:
                        "%{source.label} → %{target.label}<br>%{value:" +
                        fmt +
                        "} " +
                        unit +
                        "<extra></extra>",
                },
            },
        ];
    }

    get sankeyLayout(): any {
        // Height scales with the busier side so nodes don't cram together.
        const flows = this.displayedFlows;
        const maxNodes = Math.max(
            new Set(flows.map((r) => r.enterprise_type_orig)).size,
            new Set(flows.map((r) => r.enterprise_type_dest)).size,
            1,
        );
        return {
            // A modest top/bottom margin so the hover tooltip for the top/bottom-most node
            // isn't clipped against the chart edge, without leaving a big empty band.
            height: Math.max(380, maxNodes * 26 + 112),
            margin: { l: 8, r: 8, t: 16, b: 36 },
            font: { size: 14 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
        };
    }

    tableHeaders = [
        { text: "Type", value: "enterprise_type" },
        { text: "Category", value: "enterprise_category" },
        {
            text: "Baseline trips",
            value: "baseline_exposed_trip_count",
            align: "end",
        },
        {
            text: "Baseline tonnes",
            value: "baseline_exposed_tonnes",
            align: "end",
        },
        {
            text: "Impacted trips",
            value: "impacted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Impacted tonnes",
            value: "impacted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Rerouted trips",
            value: "rerouted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Rerouted tonnes",
            value: "rerouted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Blocked trips",
            value: "blocked_exposed_trip_count",
            align: "end",
        },
        {
            text: "Blocked tonnes",
            value: "blocked_exposed_tonnes",
            align: "end",
        },
    ];
}
